import React from "react";
import { StepIllustration } from "./StepIllustration";
import { DeviceHero } from "./DeviceHero";
import { SectionHeading } from "./SectionHeading";

/**
 * Renders a bold-marker enabled text. Supports **bold** segments.
 */
const renderRich = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => {
        if (/^\*\*[^*]+\*\*$/.test(p)) {
            return (
                <span key={i} className="font-semibold text-white">
                    {p.replace(/\*\*/g, "")}
                </span>
            );
        }
        return <React.Fragment key={i}>{p}</React.Fragment>;
    });
};

export const TabPanel = ({ data, tabId }) => {
    const isDesktop = tabId === "desktop";

    return (
        <section
            key={tabId}
            data-testid={`tab-panel-${tabId}`}
            className="fade-in"
        >
            {/* Hero block */}
            <div className="max-w-6xl mx-auto px-6 pt-14 md:pt-20 pb-6">
                <div className="grid lg:grid-cols-2 gap-10 items-center">
                    <div className="order-2 lg:order-1">
                        <h1
                            data-testid="hero-title"
                            className="text-2xl md:text-[34px] leading-tight font-bold gold-text"
                        >
                            {data.title}
                        </h1>
                        <p className="mt-6 text-[15px] md:text-base text-white/80 leading-relaxed max-w-xl">
                            {data.description}
                        </p>

                        {data.ctaLabel && (
                            <a
                                href={data.ctaHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="hero-install-btn"
                                className="btn-install mt-7"
                            >
                                {tabId === "ios" ? (
                                    <svg
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                        className="w-5 h-5"
                                    >
                                        <path d="M16.365 1.43c0 1.14-.474 2.235-1.243 3.04-.83.86-2.17 1.52-3.27 1.44-.13-1.1.4-2.244 1.16-3.04C13.83 1.94 15.22 1.4 16.365 1.43zm3.85 16.575c-.567 1.31-.838 1.895-1.568 3.054-1.02 1.62-2.456 3.64-4.234 3.658-1.58.013-1.99-1.03-4.135-1.02-2.146.012-2.6 1.04-4.18 1.027-1.778-.017-3.137-1.84-4.158-3.46C-.717 16.86-1.04 11.74 1.42 9.025c1.39-1.555 3.583-2.46 5.643-2.46 2.097 0 3.418 1.15 5.155 1.15 1.685 0 2.713-1.152 5.137-1.152 1.833 0 3.78.998 5.166 2.73-4.54 2.488-3.8 9.064-2.31 11.71z" />
                                    </svg>
                                ) : (
                                    <svg
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                        className="w-5 h-5"
                                    >
                                        <path d="M17.6 9.48l1.84-3.18a.4.4 0 00-.7-.4l-1.86 3.22A11.4 11.4 0 0012 8a11.4 11.4 0 00-4.88 1.12L5.26 5.9a.4.4 0 00-.7.4l1.84 3.18A11 11 0 001 18h22a11 11 0 00-5.4-8.52zM7 15.5a1 1 0 110-2 1 1 0 010 2zm10 0a1 1 0 110-2 1 1 0 010 2z" />
                                    </svg>
                                )}
                                {data.ctaLabel}
                            </a>
                        )}

                        {isDesktop && (
                            <div className="mt-8 inline-flex flex-col items-start">
                                <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-3 w-full max-w-[260px]">
                                    <div className="text-[9px] text-white/50 mb-1">
                                        CASINOBIAT.ONLINE
                                    </div>
                                    <div className="text-[10px] text-white/80 mb-2 truncate">
                                        casinobiat.online | Türkiye&apos;nin En
                                        İyi Online Bahis...
                                    </div>
                                    <div className="bg-[#1a1a1c] border border-[#D4AF37]/40 rounded p-3 text-center">
                                        <div className="text-[11px] font-bold text-[#D4AF37]">
                                            Favorilerim Çubuğuna
                                        </div>
                                        <div className="text-[11px] font-extrabold text-[#D4AF37]">
                                            SÜRÜKLE BIRAK
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="order-1 lg:order-2">
                        <DeviceHero name={data.deviceIllustration} />
                    </div>
                </div>
            </div>

            {/* How-to section */}
            <div className="max-w-6xl mx-auto px-6 pt-12 md:pt-16 pb-16">
                <SectionHeading testId="howto-heading">
                    {data.howTitle}
                </SectionHeading>

                <div
                    className={`mt-12 grid gap-x-8 gap-y-12 ${
                        isDesktop
                            ? "grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto"
                            : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                    }`}
                >
                    {data.steps.map((step) => (
                        <div
                            key={step.n}
                            data-testid={`step-${tabId}-${step.n}`}
                            className="flex flex-col"
                        >
                            <h4 className="text-center text-white font-semibold mb-4">
                                Adım {step.n}
                            </h4>
                            <StepIllustration name={step.illust} />
                            <p className="mt-5 text-sm text-white/70 leading-relaxed text-center">
                                {renderRich(step.caption)}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};
